import React, { useState, useEffect, useMemo } from 'react';
import { X, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from './ui/use-toast';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * RefinanceDealModal — atomic close-out + new deal creation.
 *
 * Mirrors FundDealModal's auto-calc rules so the form behaves identically:
 *   • Funding × Factor Rate → Payback (one-way)
 *   • Payback ÷ Term → Daily/Weekly Payment
 *   • Origination % is taken out of funded → net wired to merchant
 *   • Broker commission % is computed against funded amount
 *
 * Plus refinance-only fields:
 *   • payoff_to_prior — applied to the OLD deal as a manual cleared row
 *   • full_payoff — when checked, OLD deal is locked Fully Paid via Refinance
 */
const RefinanceDealModal = ({ isOpen, onClose, deal, onSuccess }) => {
  const { toast } = useToast();

  const oldRemaining = Number(deal?.remaining ?? deal?.remaining_balance ?? 0);

  const [formData, setFormData] = useState({
    funding_amount: '',
    payback_amount: '',
    factor_rate: '',
    payment_frequency: 'daily',
    term_length: '',
    payment_amount: '',
    first_payment_date: '',
    funding_date: new Date().toISOString().split('T')[0],
    broker_commission_percent: '0',
    origination_fee_percent: '0',
    payoff_to_prior: '',
    full_payoff: true,
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Reset / preseed when opening
  useEffect(() => {
    if (!isOpen || !deal) return;
    setSubmitError('');
    setFormData((prev) => ({
      ...prev,
      payoff_to_prior: oldRemaining > 0 ? oldRemaining.toFixed(2) : '',
      full_payoff: true,
      funding_date: new Date().toISOString().split('T')[0],
    }));
  }, [isOpen, deal, oldRemaining]);

  const calc = useMemo(() => {
    const funding = parseFloat(formData.funding_amount) || 0;
    const payback = parseFloat(formData.payback_amount) || 0;
    const origFeePct = parseFloat(formData.origination_fee_percent) || 0;
    const brokerPct = parseFloat(formData.broker_commission_percent) || 0;
    const payoff = parseFloat(formData.payoff_to_prior) || 0;

    const origFeeAmount = funding * (origFeePct / 100);
    const brokerAmount = funding * (brokerPct / 100);
    // NET = actual cash to merchant = funding − origination fee − payoff.
    // E.g. $75k − $7.5k − $10k = $57.5k. This is what the new deal's `net_funded`
    // is persisted as on the backend, so the chart matches what the user sees here.
    const netFunded = Math.max(funding - origFeeAmount - payoff, 0);
    const factorRate = funding > 0 ? (payback / funding).toFixed(4) : '0.0000';

    return { origFeeAmount, netFunded, brokerAmount, factorRate };
  }, [
    formData.funding_amount,
    formData.payback_amount,
    formData.origination_fee_percent,
    formData.broker_commission_percent,
    formData.payoff_to_prior,
  ]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      const funding = parseFloat(next.funding_amount) || 0;
      const factor = parseFloat(next.factor_rate) || 0;
      const term = parseInt(next.term_length) || 0;

      // Funding × Factor → Payback (only on those two fields changing)
      if ((field === 'funding_amount' || field === 'factor_rate') && funding > 0 && factor > 0) {
        next.payback_amount = (funding * factor).toFixed(2);
      }
      // Payback ÷ Term → Payment
      const payback = parseFloat(next.payback_amount) || 0;
      if (
        (field === 'funding_amount' || field === 'factor_rate' || field === 'term_length' || field === 'payback_amount')
        && payback > 0 && term > 0
      ) {
        next.payment_amount = (payback / term).toFixed(2);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    setSubmitError('');
    // Hardened validations — every miss is shown BOTH as a toast and inline
    // inside the modal so the user can never "press Refi and nothing happens".
    const fail = (msg) => {
      setSubmitError(msg);
      toast({ title: 'Cannot refinance', description: msg, variant: 'destructive' });
    };
    if (!formData.funding_amount || parseFloat(formData.funding_amount) <= 0) { fail('Funding Amount is required'); return; }
    if (!formData.payback_amount || parseFloat(formData.payback_amount) <= 0) { fail('Payback Amount is required (auto-fills when Funding × Factor are set)'); return; }
    if (!formData.factor_rate || parseFloat(formData.factor_rate) <= 0) { fail('Factor Rate is required'); return; }
    if (!formData.term_length || parseInt(formData.term_length) <= 0) { fail('Term Length (days) is required'); return; }
    if (!formData.first_payment_date) { fail('First Payment Date is required'); return; }
    const payoff = parseFloat(formData.payoff_to_prior) || 0;
    if (payoff < 0) { fail('Payoff amount cannot be negative'); return; }

    // Defensive: if auto-calc didn't run (user skipped triggers), compute
    // payment_amount from payback÷term right here so the backend never gets
    // an empty value (which would 400 with "payment_amount must be > 0").
    let computedPayment = parseFloat(formData.payment_amount) || 0;
    if (computedPayment <= 0) {
      const payback = parseFloat(formData.payback_amount) || 0;
      const term = parseInt(formData.term_length) || 0;
      computedPayment = term > 0 ? +(payback / term).toFixed(2) : 0;
    }
    if (computedPayment <= 0) { fail('Payment Amount could not be computed — check Payback & Term'); return; }

    setLoading(true);
    try {
      const token = localStorage.getItem('mcaToken');
      const res = await axios.post(`${API_BASE}/deals/${deal.display_number || deal.id}/refinance`, {
        funding_amount: formData.funding_amount,
        payback_amount: formData.payback_amount,
        factor_rate: formData.factor_rate,
        payment_frequency: formData.payment_frequency,
        term_length: formData.term_length,
        payment_amount: computedPayment,
        first_payment_date: formData.first_payment_date,
        funding_date: formData.funding_date,
        broker_commission: (calc.brokerAmount || 0).toString(),
        origination_fee: (calc.origFeeAmount || 0).toString(),
        net_funded: calc.netFunded || 0,
        payoff_to_prior: payoff,
        full_payoff: formData.full_payoff,
        notes: formData.notes || '',
      }, { headers: { Authorization: `Bearer ${token}` } });

      const newDealNum = res?.data?.new_deal?.display_number || res?.data?.new_deal?.id;
      toast({
        title: 'Refinance complete',
        description: `New deal #${newDealNum} created. ${formData.full_payoff ? 'Old deal locked Fully Paid.' : `Old deal received $${payoff.toLocaleString()} payoff.`}`,
      });
      onSuccess?.(newDealNum);
    } catch (err) {
      const apiMsg = err.response?.data?.detail || err.response?.data?.error;
      const msg = apiMsg || err.message || 'Something went wrong contacting the server';
      setSubmitError(msg);
      toast({ title: 'Refinance failed', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
      data-testid="refinance-deal-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <RefreshCw size={22} className="text-orange-600" />
              <h2 className="text-2xl font-bold text-gray-800">Refinance Deal</h2>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg mb-5">
            <p className="text-sm text-gray-800">
              <strong>Refinancing:</strong> {deal?.business || 'Unknown'} (Deal #{deal?.display_number || deal?.id})
            </p>
            <p className="text-xs text-gray-700 mt-1">
              Old remaining balance:{' '}
              <span className="font-semibold">${oldRemaining.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              {' '}— a new funded deal will be created and linked back to this one.
            </p>
          </div>

          {submitError && (
            <div
              className="mb-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
              data-testid="refi-submit-error"
            >
              <strong>Could not refinance:</strong> {submitError}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">New Funding Amount *</label>
              <input
                type="number"
                data-testid="refi-funding-amount"
                value={formData.funding_amount}
                onChange={(e) => handleChange('funding_amount', e.target.value)}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">New Payback Amount *</label>
              <input
                type="number"
                data-testid="refi-payback-amount"
                value={formData.payback_amount}
                onChange={(e) => handleChange('payback_amount', e.target.value)}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Factor Rate *</label>
              <input
                type="number"
                step="0.01"
                data-testid="refi-factor-rate"
                value={formData.factor_rate}
                onChange={(e) => handleChange('factor_rate', e.target.value)}
                className="w-full border rounded px-3 py-2"
                placeholder="1.45"
              />
              <p className="text-xs text-gray-500 mt-1">Funding × Factor = Payback</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Payment Frequency *</label>
              <select
                value={formData.payment_frequency}
                onChange={(e) => handleChange('payment_frequency', e.target.value)}
                className="w-full border rounded px-3 py-2"
                data-testid="refi-frequency"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Term (days) *</label>
              <input
                type="number"
                data-testid="refi-term-days"
                value={formData.term_length}
                onChange={(e) => handleChange('term_length', e.target.value)}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Payment Amount</label>
              <input
                type="number"
                step="0.01"
                value={formData.payment_amount}
                onChange={(e) => handleChange('payment_amount', e.target.value)}
                className="w-full border rounded px-3 py-2"
                data-testid="refi-payment-amount"
              />
              <p className="text-xs text-gray-500 mt-1">Auto = Payback ÷ Term</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">ISO/Broker Commission %</label>
              <Input
                type="number"
                step="0.1"
                data-testid="refi-broker-percent"
                value={formData.broker_commission_percent}
                onChange={(e) => handleChange('broker_commission_percent', e.target.value)}
                placeholder="20"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.broker_commission_percent || 0}% of funded = ${calc.brokerAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Origination Fee %</label>
              <Input
                type="number"
                step="0.1"
                data-testid="refi-origination-percent"
                value={formData.origination_fee_percent}
                onChange={(e) => handleChange('origination_fee_percent', e.target.value)}
                placeholder="10"
              />
              <p className="text-xs text-gray-500 mt-1">
                Out of funded: ${calc.origFeeAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Funding Date *</label>
              <input
                type="date"
                value={formData.funding_date}
                onChange={(e) => handleChange('funding_date', e.target.value)}
                className="w-full border rounded px-3 py-2"
                data-testid="refi-funding-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">First Payment Date *</label>
              <input
                type="date"
                value={formData.first_payment_date}
                onChange={(e) => handleChange('first_payment_date', e.target.value)}
                className="w-full border rounded px-3 py-2"
                data-testid="refi-first-payment-date"
              />
            </div>
          </div>

          {/* Old-deal payoff section */}
          <div className="mt-5 border-t pt-4">
            <h3 className="text-sm font-bold text-gray-800 mb-2">Payoff to Prior Deal</h3>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium mb-1">Payoff Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.payoff_to_prior}
                  onChange={(e) => handleChange('payoff_to_prior', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  data-testid="refi-payoff-amount"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Will be recorded as a cleared row on the old deal.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
                <input
                  type="checkbox"
                  checked={formData.full_payoff}
                  onChange={(e) => setFormData((p) => ({ ...p, full_payoff: e.target.checked }))}
                  data-testid="refi-full-payoff"
                />
                <span>Full payoff — lock old deal as Fully Paid (via Refinance)</span>
              </label>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
              className="w-full border rounded p-2 h-20"
              placeholder="Optional refinance notes…"
              data-testid="refi-notes"
            />
          </div>

          <div className="mt-5 bg-green-50 border border-green-200 rounded p-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <div className="flex justify-between"><span>Funding Amount:</span><span className="font-semibold">${(parseFloat(formData.funding_amount)||0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between"><span>Factor Rate:</span><span className="font-semibold">{calc.factorRate}</span></div>
            <div className="flex justify-between"><span>− Origination Fee:</span><span className="font-semibold text-red-600">−${calc.origFeeAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between"><span>− Payoff to Prior:</span><span className="font-semibold text-red-600">−${(parseFloat(formData.payoff_to_prior)||0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between col-span-2 border-t pt-1 mt-1">
              <span className="font-bold">Net to Merchant (cash wired):</span>
              <span className="font-bold text-green-700" data-testid="refi-net-to-merchant">${calc.netFunded.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between col-span-2 text-xs text-gray-600">
              <span>Broker Commission (informational):</span>
              <span>${calc.brokerAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

          <div className="flex gap-3 pt-5 border-t mt-5">
            <Button type="submit" disabled={loading} className="flex-1 bg-orange-600 hover:bg-orange-700" data-testid="refi-submit">
              {loading ? 'Refinancing…' : 'Refinance Deal'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RefinanceDealModal;
